import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { AddressFieldMaxLength } from "@shared";
import { insuranceNumberValidation } from "@shared";
import { ClientPersonalDataMaxLength } from "@shared";
import { phoneNumberValidation } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { ContactDetailType, DropdownCategory, UpdateClientPersonalDataRequest, clientsApi } from "@/api";
import { DropdownCategoryMaxLength } from "@/common/constants/dropdownCategoryMaxLength";
import { EmailPattern, ZipCodePattern } from "@/common/helper/validationPatterns";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { EditClientType } from "../EditClient.types";

export function useClientPersonalDataEdit(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const dropdownOptions = useDropdownOptions();

  const { data: personalData } = useQuery({
    queryKey: [QueryKey.ClientPersonalData, clientId],
    queryFn: () => clientsApi.getClientPersonalData({ clientId }),
  });

  const updateMutation = useMutation({
    mutationFn: (request: UpdateClientPersonalDataRequest) =>
      clientsApi.updateClientPersonalData({
        clientId,
        updateClientPersonalDataRequest: request,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientPersonalData, clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Client, clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Clients] });
      showSuccess(t("clients.client.masterData.personalData.successfullySaved"));
    },
    onError: (error) => {
      showError(error.message);
    },
  });

  const validationSchema = useMemo(() => {
    const minimalBirthDate = new Date(new Date().setFullYear(new Date().getFullYear() - 120));

    return Yup.object().shape({
      contactSource: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.contactSource, ({ max }) => t("common.validation.maxLength", { max })),
      comment: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.comment, ({ max }) => t("common.validation.maxLength", { max })),
      salutation: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.salutation") }))
        .max(DropdownCategoryMaxLength[DropdownCategory.Salutation], ({ max }) =>
          t("common.validation.maxLength", { max })
        ),
      title: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.title, ({ max }) => t("common.validation.maxLength", { max })),
      firstName: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.name") }))
        .max(ClientPersonalDataMaxLength.firstName, ({ max }) => t("common.validation.maxLength", { max })),
      lastName: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.lastName") }))
        .max(ClientPersonalDataMaxLength.lastName, ({ max }) => t("common.validation.maxLength", { max })),
      formerLastName: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.formerLastName, ({ max }) => t("common.validation.maxLength", { max })),
      placeOfBirth: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.placeOfBirth, ({ max }) => t("common.validation.maxLength", { max })),
      street: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.street") }))
        .max(ClientPersonalDataMaxLength.street, ({ max }) => t("common.validation.maxLength", { max })),
      houseNumber: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.houseNumber") }))
        .max(AddressFieldMaxLength.houseNumber, ({ max }) => t("common.validation.maxLength", { max })),
      zipCode: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.zipCode") }))
        .matches(ZipCodePattern, t("clients.create.invalidZipCode"))
        .min(4, ({ min }) => t("common.validation.minLength", { min }))
        .max(AddressFieldMaxLength.zipCode, ({ max }) => t("common.validation.maxLength", { max })),
      city: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.city") }))
        .max(AddressFieldMaxLength.city, ({ max }) => t("common.validation.maxLength", { max })),
      country: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.country") }))
        .max(ClientPersonalDataMaxLength.country, ({ max }) => t("common.validation.maxLength", { max })),
      emailContactDetails: Yup.array().of(
        Yup.object().shape({
          type: Yup.mixed<ContactDetailType>().oneOf([ContactDetailType.Email]).required(),
          // "comment" here refers to the contact detail's own comment field (not the top-level client comment)
          value: Yup.string()
            .max(ClientPersonalDataMaxLength.contactDetail.value, ({ max }) =>
              t("common.validation.maxLength", { max })
            )
            .when("comment", {
              is: (comment: string | null) => !!comment,
              then: (schema) => schema.required(t("clients.client.masterData.personalData.emailRequired")),
            })
            .matches(EmailPattern, {
              message: t("clients.create.invalidEmail"),
              excludeEmptyString: true,
            }),
          comment: Yup.string()
            .nullable()
            .max(ClientPersonalDataMaxLength.contactDetail.comment, ({ max }) =>
              t("common.validation.maxLength", { max })
            ),
        })
      ),
      phoneContactDetails: Yup.array().of(
        Yup.object().shape({
          type: Yup.mixed<ContactDetailType>().oneOf([ContactDetailType.Phone]).required(),
          // "comment" here refers to the contact detail's own comment field (not the top-level client comment)
          value: phoneNumberValidation(t)
            .max(ClientPersonalDataMaxLength.contactDetail.value, ({ max }) =>
              t("common.validation.maxLength", { max })
            )
            .when("comment", {
              is: (comment: string | null) => !!comment,
              then: (schema) => schema.required(t("clients.client.masterData.personalData.phoneRequired")),
            }),
          comment: Yup.string()
            .nullable()
            .max(ClientPersonalDataMaxLength.contactDetail.comment, ({ max }) =>
              t("common.validation.maxLength", { max })
            ),
        })
      ),
      birthday: Yup.date()
        .nullable()
        .typeError(t("common.validation.invalidDateFormat"))
        .min(minimalBirthDate, t("clients.create.birthdayTooOld"))
        .max(new Date(), t("clients.create.dateMaxToday")),
      employmentStatus: Yup.string().required(
        t("common.validation.fieldRequired", { fieldName: t("clients.create.employmentStatus") })
      ),
      initialRegistrationDate: Yup.date()
        .typeError(t("common.validation.invalidDateFormat"))
        .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.creationDate") })),
      ...insuranceNumberValidation(t),
    });
  }, [t]);

  const initialValues: EditClientType = useMemo(() => {
    if (!personalData) {
      return {
        initialRegistrationDate: new Date(),
        contactSource: null,
        comment: null,
        salutation: "",
        title: null,
        firstName: "",
        lastName: "",
        formerLastName: null,
        street: "",
        houseNumber: "",
        zipCode: "",
        city: "",
        country: "",
        emailContactDetails: [{ id: null, type: ContactDetailType.Email, value: "", comment: null }],
        phoneContactDetails: [{ id: null, type: ContactDetailType.Phone, value: "", comment: null }],
        birthday: null,
        insuranceNumber: null,
        employmentStatus: "",
        qualification: null,
        placeOfBirth: null,
        nationality: null,
        maritalStatus: null,
        livingSituation: null,
        mediaInquiry: null,
      };
    }

    const mapContactsByType = (type: ContactDetailType) => {
      const existing = personalData.contactDetails.filter((e) => e.type === type);
      return existing.length > 0
        ? existing.map((e) => ({ id: e.id, type: e.type, value: e.value, comment: e.comment }))
        : [{ id: null, type, value: "", comment: null }];
    };

    return {
      comment: personalData.comment,
      salutation: personalData.salutation ?? "",
      title: personalData.title,
      firstName: personalData.firstName,
      lastName: personalData.lastName,
      formerLastName: personalData.formerLastName,
      street: personalData.street,
      houseNumber: personalData.houseNumber,
      zipCode: personalData.zipCode,
      city: personalData.city,
      country: personalData.country,
      emailContactDetails: mapContactsByType(ContactDetailType.Email),
      phoneContactDetails: mapContactsByType(ContactDetailType.Phone),
      insuranceNumber: personalData.insuranceNumber,
      birthday: personalData.birthday,
      employmentStatus: personalData.employmentStatus ?? "",
      initialRegistrationDate: personalData.initialRegistrationDate,
      contactSource: personalData.contactSource,
      qualification: personalData.qualification,
      placeOfBirth: personalData.placeOfBirth,
      nationality: personalData.nationality,
      maritalStatus: personalData.maritalStatus,
      livingSituation: personalData.livingSituation,
      mediaInquiry: personalData.mediaInquiry,
    };
  }, [personalData]);

  const formik = useFormikWithDirtyForm<EditClientType>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const request: UpdateClientPersonalDataRequest = {
        comment: values.comment,
        salutation: values.salutation || null,
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        formerLastName: values.formerLastName,
        street: values.street,
        houseNumber: values.houseNumber,
        zipCode: values.zipCode,
        city: values.city,
        country: values.country,
        contactDetails: [...values.emailContactDetails, ...values.phoneContactDetails].filter(
          (e) => e.value.trim() !== ""
        ),
        insuranceNumber: values.insuranceNumber,
        birthday: values.birthday,
        employmentStatus: values.employmentStatus || null,
        initialRegistrationDate: values.initialRegistrationDate,
        contactSource: values.contactSource,
        qualification: values.qualification,
        placeOfBirth: values.placeOfBirth,
        nationality: values.nationality,
        maritalStatus: values.maritalStatus,
        livingSituation: values.livingSituation,
        mediaInquiry: values.mediaInquiry,
      };

      await updateMutation.mutateAsync(request);
    },
  });

  return { formik, dropdownOptions, personalData };
}
